import React, { createContext, useContext, useEffect, useState } from "react";
import { getSymbols } from "../../../utils/api";
import { getOptionData } from "../ApiCalls/FetchExpirySymbols";
import WebsocketWrapperContext from "../../../context/WebsocketWrapperContext";
import { getAllOptionCodesFromAllOptionsData } from "../helpers/GetAllCodes";

const BuilderContext = createContext(null);
export const useBuilderContext = () => useContext(BuilderContext);

export const BuilderProvider = ({ children }) => {
  const {
    checkWebsocketConnection,
    subscribeToSymbols,
    unsubscribeToSymbols,
    symbolList,
    symbolData,
  } = useContext(WebsocketWrapperContext);

  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [searchSymbol, setSearchSymbol] = useState(null);
  const [allSymbols, setAllSymbols] = useState(null);
  const [allOptionsData, setAlloptionsData] = useState({});
  const [currentOptionsData, setCurrentOptionsData] = useState({});
  const [currentFuturesData, setCurrentFuturesData] = useState({});
  const [rows, setRows] = useState({});
  const [selectedData, setSelectedData] = useState([]);
  const [selectedExpiry, setSelectedExpiry] = useState("");
  const [symbolsActiveCategory, setSymbolsActiveCategory] = useState({});
  const [symbolsSelectedStrategy, setSymbolsSelectedStrategy] = useState({});
  const [subscribedOptionCodes, setSubscribedOptionCodes] = useState([]);
  const [limitOrderStates, setLimitOrderStates] = useState({});
  const [lotx, setLotx] = useState(1);
  const [lotxMap, setLotxMap] = useState({});
  const [marketType, setMarketType] = useState("NRML");
  const [marketTypeMap, setMarketTypeMap] = useState({});
  const [activeTab, setActiveTab] = useState("Nifty");
  const [configureSettingsMap, setConfigureSettingsMap] = useState({
    showCalls: true,
    showPuts: true,
    showDelta: false,
    showTheta: false,
    showGamma: false,
    showVega: false,
    showOIChng: false,
    showOIBuilddup: false,
    noOfStrikes: 30,
    maxStrikes: 30,
  });

  useEffect(() => {
    if (!currentOptionsData[selectedExpiry]) return;
    const optionsLength =
      currentOptionsData[selectedExpiry]?.options?.length || 0;

    setConfigureSettingsMap((prev) => ({
      ...prev,
      maxStrikes: optionsLength,
    }));
  }, [currentOptionsData[selectedExpiry]]);

  useEffect(() => {
    const currentLotx = selectedSymbol ? lotxMap[selectedSymbol.scrip] ?? 1 : 1;
    setLotx(currentLotx);
  }, [selectedSymbol, lotxMap]);

  useEffect(() => {
    const currentMarketType = selectedSymbol
      ? marketTypeMap[selectedSymbol.scrip] ?? "NRML"
      : "NRML";
    setMarketType(currentMarketType);
  }, [selectedSymbol, marketTypeMap]);

  const delay = (ms) => new Promise((res) => setTimeout(res, ms));

  useEffect(() => {
    const runEffect = async () => {
      let retries = 3;
      while (!selectedSymbol && retries > 0) {
        await delay(100);
        retries--;
      }
      if (!selectedSymbol) return;

      subscribeToSymbols(
        [selectedSymbol.code],
        selectedSymbol.exchange || "NSE"
      );

      setSelectedExpiry(selectedSymbol?.option_expiries?.[0]);

      const existingData = allOptionsData[selectedSymbol?.scrip];
      if (existingData) return;

      // const fetchAllOptions = async () => {
      //   if (!selectedSymbol?.option_expiries || !selectedSymbol.scrip) return;

      //   const expiryDates = selectedSymbol.option_expiries;
      //   const requests = expiryDates.map(async (date) => {
      //     const formattedDate = date.replaceAll(" ", "%20");
      //     // console.log(selectedSymbol.scrip + formattedDate);
      //     const data = await getOptionData(selectedSymbol.scrip, formattedDate);
      //     return { [date]: data };
      //   });

      //   const results = await Promise.all(requests);
      //   const mergedData = Object.assign({}, ...results);

      //   setAlloptionsData((prev) => ({
      //     ...prev,
      //     [selectedSymbol.scrip]: mergedData,
      //   }));

      //   // const codes = getAllOptionCodesFromAllOptionsData(mergedData, selectedSymbol?.option_expiries?.[0]);
      //   // updateOptionSubscriptions(codes);
      // };

      // const fetchAllOptions = async () => {
      //   if (!selectedSymbol?.option_expiries || !selectedSymbol.scrip) return;

      //   // Combine option and futures expiries
      //   const allExpiries = [
      //     ...selectedSymbol.option_expiries,
      //     ...(selectedSymbol.fut_expiries || []),
      //   ];

      //   // Remove duplicates
      //   const uniqueExpiries = [...new Set(allExpiries)];

      //   const requests = uniqueExpiries.map(async (date) => {
      //     const formattedDate = date.replaceAll(" ", "%20");
      //      // console.log(
      //        "Fetching data for",
      //        selectedSymbol.scrip,
      //        formattedDate
      //      );
      //     const data = await getOptionData(selectedSymbol.scrip, formattedDate);
      //      // console.log("API response for", date, ":", data);
      //     return {
      //       [date]: processExpiryData(data),
      //     };
      //   });

      //   try {
      //     const results = await Promise.all(requests);
      //     const mergedData = Object.assign({}, ...results);

      //     setAlloptionsData((prev) => ({
      //       ...prev,
      //       [selectedSymbol.scrip]: mergedData,
      //     }));
      //   } catch (error) {
      //     console.error("Error fetching expiry data:", error);
      //   }
      // };

      const fetchAllOptions = async () => {
        if (!selectedSymbol?.option_expiries || !selectedSymbol.scrip) return;

        // Combine option and futures expiries
        const allExpiries = [
          ...selectedSymbol.option_expiries,
          ...(selectedSymbol.fut_expiries || []),
        ];
        // Remove duplicates
        const uniqueExpiries = [...new Set(allExpiries)];

        const requests = uniqueExpiries.map(async (date) => {
          const formattedDate = date.replaceAll(" ", "%20");
          // console.log("Fetching data for", selectedSymbol.scrip, formattedDate);

          try {
            const data = await getOptionData(
              selectedSymbol.scrip,
              formattedDate
            );
            // // console.log("API response for", date, ":", data);
            return {
              [date]: processExpiryData(data),
            };
          } catch (error) {
            console.error("API error for", date, ":", error);
            return { [date]: processExpiryData({}) };
          }
        });

        try {
          const results = await Promise.all(requests);
          // Merge all the objects into a single object
          const mergedData = Object.assign({}, ...results);

          setAlloptionsData((prev) => ({
            ...prev,
            [selectedSymbol.scrip]: mergedData,
          }));
        } catch (error) {
          console.error("Error fetching expiry data:", error);
        }
      };
      fetchAllOptions();
    };

    runEffect();
  }, [selectedSymbol]);

  //subscribing codes

  // const updateOptionSubscriptions = (optionCodes) => {
  //     if (subscribedOptionCodes.length > 0) {
  //         unsubscribeToSymbols(subscribedOptionCodes, selectedSymbol.exchange);
  //     }
  //     if (optionCodes.length > 0) {
  //         subscribeToSymbols(optionCodes, selectedSymbol.exchange);
  //         setSubscribedOptionCodes(optionCodes);
  //     }
  // };

  // useEffect(() => {
  //     const existingData = allOptionsData[selectedSymbol?.scrip];
  //     if (currentOptionsData) {
  //         const codes = getAllOptionCodesFromAllOptionsData(currentOptionsData, selectedExpiry);
  //         updateOptionSubscriptions(codes);
  //         // console.log(codes, 'codes')
  //     }
  // }, [currentOptionsData])

  //updating current options data
  useEffect(() => {
    if (!selectedSymbol || !allOptionsData[selectedSymbol?.scrip]) return;
    
    const processedSymbolData = getProcessedSymbolData(selectedSymbol?.scrip);
    if (!processedSymbolData) return;
    
    setCurrentOptionsData(processedSymbolData);
  }, [selectedSymbol, allOptionsData]);

  //store all symbols and set initial symbol
  useEffect(() => {
    const fetchSymbols = async () => {
      const symbols = await getSymbols();
      if (!symbols) return;

      // Always default to NIFTY for Strategy Creator, regardless of stored symbol
      const niftySymbol = symbols.find((symbol) => symbol.scrip === "NIFTY");
      setSelectedSymbol(niftySymbol);
      // console.log("symbolsss", symbols, niftySymbol);
      setAllSymbols(symbols);
    };
    fetchSymbols();
  }, []);

  //basket logic

  // Sync selectedData with rows
  useEffect(() => {
    const currentRows = rows[selectedSymbol?.scrip] || [];
    setSelectedData(currentRows);
  }, [rows, selectedSymbol]);

  // Update prices from websocket for Market orders only
  useEffect(() => {
    const currentRows = rows[selectedSymbol?.scrip] || [];
    if (!currentRows.length) return;

    // Check if any Market order needs price update
    const needsUpdate = currentRows.some(row =>
      row.orderType !== "L" && symbolData[row.code]?.ltp && !row.price
    );

    if (!needsUpdate) return;

    // Create new array with updated prices for Market orders only
    const updatedRows = currentRows.map(row => {
      if(symbolData[row.code] && row.orderType !== "L"){
        return { ...row, price: symbolData[row.code]?.ltp };
      }
      return row;
    });

    setRows(prev => ({
      ...prev,
      [selectedSymbol?.scrip]: updatedRows,
    }));
  }, [symbolData, selectedSymbol]);

  const getPreferredFutExpiry = (symbol) => {
    if (!symbol) return null;

    // Use weekly expiry for NIFTY, first expiry for others
    if (symbol.scrip === "NIFTY") {
      return (
        symbol.fut_expiries?.find(
          (expiry) => expiry.includes("Weekly") || expiry.includes("week")
        ) || symbol.fut_expiries?.[0]
      );
    }
    return symbol.fut_expiries?.[0];
  };

  // Effect to update currentFuturesData
  useEffect(() => {
    if (!selectedSymbol || !allOptionsData[selectedSymbol.scrip]) {
      setCurrentFuturesData(null);
      return;
    }

    // Use preferred expiry (weekly for NIFTY)
    const preferredFutExpiry = getPreferredFutExpiry(selectedSymbol);
    if (!preferredFutExpiry) {
      setCurrentFuturesData(null);
      return;
    }

    const normalizedExpiry = normalizeExpiryDate(preferredFutExpiry);
    const processedSymbolData = getProcessedSymbolData(selectedSymbol.scrip);

    if (!processedSymbolData) {
      // console.log("symbolData is empty or invalid:", allOptionsData[selectedSymbol.scrip]);
      setCurrentFuturesData(null);
      return;
    }

    // Find matching expiry data
    const expiryKey = Object.keys(processedSymbolData).find(
      (key) => normalizeExpiryDate(key) === normalizedExpiry
    );

    if (!expiryKey) {
      // console.log("symbolData", allOptionsData[selectedSymbol.scrip]);
      // console.log("processedSymbolData", processedSymbolData);
      console.warn("No expiry data found for", preferredFutExpiry);
      setCurrentFuturesData(null);
      return;
    }

    const expiryData = processedSymbolData[expiryKey];
    const futureInstrument =
      expiryData.futures?.find(
        (fut) => fut.underlying_symbol === selectedSymbol.symbol
      ) || expiryData.futures?.[0];

    setCurrentFuturesData(futureInstrument);
  }, [selectedSymbol, allOptionsData]);

  //changes 30/07
  // Add this helper function inside your context

  const processExpiryData = (response) => {
    // Handle different response structures
    let futures = [];
    let options = [];
    let lot_size = 50;

    // Handle null/undefined response
    if (!response) {
      console.warn("processExpiryData received null/undefined response");
      return { options, futures, lot_size };
    }

    if (Array.isArray(response)) {
      // Old format: pure array
      if (response.length === 0) {
        console.warn("processExpiryData received empty array");
        return { options, futures, lot_size };
      }
      
      futures = response.filter((i) => i.instrument_type === "FUT");
      options = response.filter(
        (i) => i.option_type === "CE" || i.option_type === "PE"
      );
      lot_size = response[0]?.lot_size || 50;
    } else if (response && typeof response === "object") {
      // New format: object with futures/options
      futures = response.futures || [];
      options = response.options || [];
      lot_size = response.lot_size || 50;

      // Also check if there's a nested array (your log shows this)
      if (Array.isArray(response[0])) {
        futures = [
          ...futures,
          ...response[0].filter((i) => i.instrument_type === "FUT"),
        ];
      }
    } else {
      console.warn("processExpiryData received unexpected response type:", typeof response, response);
    }

    return {
      options,
      futures,
      lot_size,
    };
  };

  // Add this helper function
  const normalizeExpiryDate = (dateStr) => {
    try {
      const parsed = new Date(dateStr);
      return parsed.toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch (e) {
      console.error("Error parsing date:", dateStr, e);
      return dateStr;
    }
  };

  // Helper function to safely get processed symbol data
  const getProcessedSymbolData = (scrip) => {
    const symbolData = allOptionsData[scrip];
    if (!symbolData) return null;
    
    // Handle case where symbolData might be an array
    if (Array.isArray(symbolData)) {
      return Object.assign({}, ...symbolData);
    }
    
    return symbolData;
  };
  // Add this to your context value
  const getFirstFutureInstrument = () => {
    if (!selectedSymbol?.fut_expiries?.[0]) return null;

    const futExpiry = selectedSymbol.fut_expiries[0];
    const normalizedExpiry = normalizeExpiryDate(futExpiry);

    const processedSymbolData = getProcessedSymbolData(selectedSymbol.scrip);
    if (!processedSymbolData) return null;

    // Try both date formats
    const expiryData = processedSymbolData[futExpiry] || processedSymbolData[normalizedExpiry];
    if (!expiryData) return null;

    return expiryData.futures?.[0] || null;
  };

  const handleLotxChange = (newLotx, reset) => {
    if (newLotx === lotx) return;

    let updatedData = selectedData;

    if (newLotx !== 1 || reset == null) {
      updatedData = selectedData.map((obj) => {
        const initial_lot = obj.lotSize / lotx;
        const updated_lot = Math.round(initial_lot * newLotx);
        return { ...obj, lotSize: updated_lot };
      });
    }

    setRows((prev) => ({
      ...prev,
      [selectedSymbol?.scrip]: updatedData,
    }));

    setLotxMap((prev) => ({
      ...prev,
      [selectedSymbol?.scrip]: newLotx,
    }));
  };

  const handleLotSizeChange = (index, value) => {
    handleLotxChange(1, true);
    if (!selectedData || selectedData.length === 0) return;

    const updatedRows = selectedData.map((row, i) =>
      i === index ? { ...row, lotSize: Number(value) } : row
    );

    setRows((prev) => ({
      ...prev,
      [selectedSymbol?.scrip]: updatedRows,
    }));
  };

  const handleMarketTypeChange = (newType) => {
    if (newType === marketType) return;
    setMarketTypeMap((prev) => ({
      ...prev,
      [selectedSymbol?.scrip]: newType,
    }));
  };

  const clearBasket = () => {
    setRows((prev) => ({
      ...prev,
      [selectedSymbol?.scrip]: [],
    }));
    setMarketTypeMap((prev) => ({
      ...prev,
      [selectedSymbol?.scrip]: "NRML",
    }));
    setLotxMap((prev) => ({
      ...prev,
      [selectedSymbol?.scrip]: 1,
    }));
    setLimitOrderStates((prev) => ({
      ...prev,
      [selectedSymbol?.scrip]: [],
    }));
    setSymbolsSelectedStrategy((prev) => ({
      ...prev,
      [selectedSymbol.scrip]: null,
    }));
  };

  // // console.log("currentFuturesData", currentFuturesData);

  return (
    <BuilderContext.Provider
      value={{
        selectedSymbol,
        setSelectedSymbol,
        searchSymbol,
        setSearchSymbol,
        allSymbols,
        setAllSymbols,
        allOptionsData,
        setAlloptionsData,
        currentOptionsData,
        setCurrentOptionsData,
        rows,
        setRows,
        selectedData,
        setSelectedData,
        selectedExpiry,
        setSelectedExpiry,
        symbolsActiveCategory,
        setSymbolsActiveCategory,
        symbolsSelectedStrategy,
        setSymbolsSelectedStrategy,
        limitOrderStates,
        setLimitOrderStates,
        lotx,
        setLotx,
        handleLotxChange,
        lotxMap,
        setLotxMap,
        marketTypeMap,
        setMarketTypeMap,
        marketType,
        setMarketType,
        handleMarketTypeChange,
        clearBasket,
        activeTab,
        setActiveTab,
        configureSettingsMap,
        setConfigureSettingsMap,
        handleLotSizeChange,
        getFirstFutureInstrument,
        normalizeExpiryDate,
        currentFuturesData,
        processExpiryData,
        getProcessedSymbolData,
      }}>
      {children}
    </BuilderContext.Provider>
  );
};
